import React, { useState, useEffect } from 'react';

import { observer } from 'mobx-react';
import { Modal, Notification } from '@bringg/react-components';
import { useTranslation } from 'react-i18next';
import { Run } from '@bringg/types';

import VehicleTrailerList, { SelectedVehicleData } from './vehicle-trailer-list';
import VehicleAssignerModalHeader from './vehicle-assigner-modal-header';
import VehicleAssignerModalFooter from './vehicle-assigner-modal-footer';
import { useStores } from '../../../recipes';
import * as trailersProvider from '../../../stores/vehicles/trailers-provider';
import {
	ButtonsLoading,
	initializeButtonsLoading,
	ModalParams,
	trailerTranslation,
	VehicleRealtimeAsyncEvent,
	vehicleTranslation
} from './vehicle-assigner-modal-const';

import './vehicle-assigner-modal.scss';

const vehicleAssignErrorReasonCode = {
	16: 'RUN_VEHICLE_ASSIGNMENT_ERROR.CANT_ASSIGN_POWER_UNIT_TO_TASK'
};

export interface VehicleAssignerModalProps {
	onClose: () => void;
	modalParams: ModalParams;
	startRealtimeAsync?: (eventData: VehicleRealtimeAsyncEvent) => {
		onActionSuccess: () => void;
		onActionFail: () => void;
	};
}

const VehicleAssignerModal: React.FC<VehicleAssignerModalProps> = ({
	onClose,
	modalParams,
	startRealtimeAsync
}: VehicleAssignerModalProps) => {
	const { t } = useTranslation();
	const { vehiclesStore, vehicleTypesStore, runStore, driversStore } = useStores();
	const [selectedVehicle, setSelectedVehicle] = useState<Partial<SelectedVehicleData> | null>(null);
	const [selectedTrailer, setSelectedTrailer] = useState<Partial<SelectedVehicleData> | null>(null);
	const [totalVehiclesWeight, setTotalVehiclesWeight] = useState<number>(0);
	const [areChosenVehiclesHaveMatchSkills, setAreChosenVehiclesHaveMatchSkills] = useState<boolean | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [buttonsLoading, setButtonsLoading] = useState<ButtonsLoading>(initializeButtonsLoading);
	const [run, setRun] = useState<Run>(null);
	const { runId, teamIds, userId, tasks, vehicleId, trailerId } = modalParams.data;

	useEffect(() => {
		async function fetchAll() {
			setIsLoading(true);
			const promises: Promise<any>[] = [vehiclesStore.loadAllByTeam(teamIds[0]), vehicleTypesStore.fetchAll()];

			const loadedRun = runStore.get(runId);

			if (loadedRun) {
				setRun(loadedRun);
			} else {
				promises.push(runStore.load(runId).then(run => setRun(run)));
			}

			if (userId) {
				promises.push(driversStore.fetch(userId));
			}
			try {
				await Promise.all(promises);
			} catch (e) {
				console.error('Problem with fetchAll', e);
			}
			setSelectedVehicle({ id: vehicleId });
			setSelectedTrailer({ id: trailerId });
			setIsLoading(false);
		}

		fetchAll();
	}, []);

	useEffect(() => {
		setTotalVehiclesWeight(getChosenTotalWeight());

		let isVehiclesHaveMatchSkills = true;
		let isEnteredToCondition = false;
		if (selectedVehicle && Object.prototype.hasOwnProperty.call(selectedVehicle, 'isSkillsMatch')) {
			isVehiclesHaveMatchSkills = selectedVehicle.isSkillsMatch;
			isEnteredToCondition = true;
		}
		if (selectedTrailer && Object.prototype.hasOwnProperty.call(selectedTrailer, 'isSkillsMatch')) {
			isVehiclesHaveMatchSkills = isVehiclesHaveMatchSkills || selectedTrailer.isSkillsMatch;
		}
		if (!isEnteredToCondition) {
			isVehiclesHaveMatchSkills = null;
		}
		setAreChosenVehiclesHaveMatchSkills(isVehiclesHaveMatchSkills);
	}, [selectedVehicle, selectedTrailer]);

	const canAttachTrailer = () => {
		return vehicleTypesStore?.get(selectedVehicle?.vehicle_type_id)?.can_attach_trailer;
	};

	useEffect(() => {
		if (!canAttachTrailer()) {
			setSelectedTrailer(null);
		}
	}, [canAttachTrailer()]);

	const getChosenTotalWeight = () => {
		const vehicleMaxTotalWeight = vehicleTypesStore.get(selectedVehicle?.vehicle_type_id)?.max_total_weight || 0;
		const trailerMaxTotalWeight = vehicleTypesStore.get(selectedTrailer?.vehicle_type_id)?.max_total_weight || 0;
		return vehicleMaxTotalWeight + trailerMaxTotalWeight;
	};

	const initRealTimeAsync = (eventData: VehicleRealtimeAsyncEvent) => {
		let onActionFail, onActionSuccess;

		if (typeof startRealtimeAsync === 'function') {
			({ onActionSuccess, onActionFail } = startRealtimeAsync(eventData));
		}

		return { onActionFail, onActionSuccess };
	};

	const assignVehicles = async (_trailerId: number) => {
		const { onActionFail, onActionSuccess } = initRealTimeAsync({
			vehicleId: selectedVehicle.id,
			trailerId: _trailerId || null,
			isPowerUnit: !selectedVehicle.max_total_weight
		});
		const tasksAndVehicleIds = tasks.map(task => ({ id: task.id, vehicle_id: _trailerId }));

		try {
			if (modalParams.doNotNotifyServerOnAssign) {
				modalParams.handleListUpdate?.({ runId, vehicleId: selectedVehicle.id, tasksIds: tasksAndVehicleIds });
				onClose?.();
				return;
			}

			const result = await runStore.assignVehicles(runId, selectedVehicle.id, tasksAndVehicleIds);

			if (result.success) {
				modalParams.handleListUpdate?.({ runId, vehicleId: selectedVehicle.id, tasksIds: tasksAndVehicleIds });
				onActionSuccess?.();
				Notification.success(t('RUN_VEHICLE_ASSIGNMENT.ASSIGN_SUCCESS'));
				onClose();
			} else {
				onActionFail?.();
				Notification.error(t('RUN_VEHICLE_ASSIGNMENT.ASSIGN_FAILED'));
				console.error('Failed to assign vehicles', result.message);
			}

			return result.success;
		} catch (e) {
			console.error('failed to assign driver', e);
			const assignErrorReason = vehicleAssignErrorReasonCode[e['rc']] || 'RUN_VEHICLE_ASSIGNMENT.ASSIGN_FAILED';
			Notification.error(t(assignErrorReason));
			return false;
		}
	};

	const okButton = async () => {
		if (selectedVehicle.id === vehicleId && selectedTrailer?.id === trailerId) {
			return;
		}

		setButtonsLoading(prev => ({ ...prev, ok: true }));

		const success = await assignVehicles(selectedTrailer?.id);

		!success && setButtonsLoading(prev => ({ ...prev, ok: false }));
	};

	const failedToUnassign = error => {
		Notification.error(t('DISPTACH.ERROR_UNASSIGNING_VEHICLE_FROM_RUN'));
		console.error('Failed to unassign vehicles', error);
		setButtonsLoading(prev => ({ ...prev, unassignVehicles: false }));
	};

	const unassignVehicles = async () => {
		const { onActionFail, onActionSuccess } = initRealTimeAsync({
			vehicleId: null,
			trailerId: null,
			isPowerUnit: !selectedVehicle.max_total_weight
		});

		setButtonsLoading(prev => ({ ...prev, unassignVehicles: true }));

		try {
			const result = await runStore.unassignVehicles(runId);

			if (result.success) {
				modalParams.handleListUpdate?.({ runId, vehicleId: undefined, tasksIds: [] });
				onActionSuccess?.();
				Notification.success(t('DISPTACH.RUN_VEHICLE_UNASSIGNED'));
				onClose();
			} else {
				onActionFail?.();
				failedToUnassign(result);
			}
		} catch (e) {
			onActionFail?.();
			failedToUnassign(e);
		}
	};

	const unassignTrailer = async () => {
		setButtonsLoading(prev => ({ ...prev, unassignTrailer: true }));

		const success = await assignVehicles(null);

		!success && setButtonsLoading(prev => ({ ...prev, unassignTrailer: false }));
	};

	const trailerRenderCondition =
		!isLoading &&
		trailersProvider.allTrailersByTeam(teamIds[0]).length > 0 &&
		(selectedVehicle?.id || selectedTrailer?.id);

	const modalTitle = () => {
		if (runId == null) {
			return null;
		}

		const routeName = run ? `: ${run.route_name || run.external_id || run.id}` : '';
		return (
			<span className="vehicle-assignment-modal-title">
				{t('ROUTE_VEHICLE_ASSIGNMENT_MODAL.TITLE')}
				{routeName}
			</span>
		);
	};

	const teamHaveTrailer = trailersProvider.allTrailersByTeam(teamIds[0])?.length > 0;
	const maxListHeight = !trailerRenderCondition ? 400 : undefined;

	return (
		<Modal
			title={modalTitle()}
			visible
			width={800}
			onCancel={() => onClose()}
			footer={
				<VehicleAssignerModalFooter
					modalParams={modalParams}
					selectedVehicle={selectedVehicle}
					selectedTrailer={selectedTrailer}
					unassignVehicles={unassignVehicles}
					unassignTrailer={unassignTrailer}
					buttonsLoading={buttonsLoading}
					okButton={okButton}
				/>
			}
			zIndex={1060}
			bodyStyle={{ height: '590px' }}
			className="vehicle-assignment-modal"
		>
			<VehicleAssignerModalHeader
				areChosenVehiclesHaveMatchSkills={areChosenVehiclesHaveMatchSkills}
				tasksWeight={modalParams.data.weight?.value}
				totalVehiclesWeight={totalVehiclesWeight}
				teamHaveTrailer={teamHaveTrailer}
			/>

			<VehicleTrailerList
				translations={vehicleTranslation(t)}
				modalParams={modalParams}
				isTrailer={false}
				selectedItemId={selectedVehicle?.id}
				setSelectedItem={setSelectedVehicle}
				isLoading={isLoading}
				maxHeight={maxListHeight}
			/>
			{trailerRenderCondition && (
				<VehicleTrailerList
					translations={trailerTranslation(t)}
					modalParams={modalParams}
					isTrailer
					selectedItemId={selectedTrailer?.id}
					setSelectedItem={setSelectedTrailer}
					isLoading={isLoading}
					disabled={!canAttachTrailer()}
					matchSkillsWith={selectedVehicle}
				/>
			)}
		</Modal>
	);
};
export default observer(VehicleAssignerModal);
